type ConvesioPayTokenResult = string | {token?: string; id?: string};

export type ConvesioPayCardComponent = {
	createToken?: () => ConvesioPayTokenResult | Promise<ConvesioPayTokenResult>;
};

type ConvesioPayCardState = {
	isValid: boolean;
	errors: unknown[];
	lastEvent: unknown;
};

type ConvesioPayMethod = 'card' | 'applepay' | 'btcpay' | 'googlepay';

const emptyCardState: ConvesioPayCardState = {
	isValid: false,
	errors: [],
	lastEvent: null,
};

let cardState: ConvesioPayCardState = {...emptyCardState};
let cachedCardToken = '';
let cardStateRevision = 0;
let tokenizationInFlight: Promise<string> | null = null;
let isCardTokenizationActive = false;

function clearCachedConvesioPayCardToken(): void {
	if (cachedCardToken && (window as unknown as Record<string, unknown>)['convesiopayPaymentToken'] === cachedCardToken) {
		(window as unknown as Record<string, unknown>)['convesiopayPaymentToken'] = undefined;
	}

	cachedCardToken = '';
}

function hasConvesioPayCardStateChanged(nextCardState: ConvesioPayCardState): boolean {
	return cardState.isValid !== nextCardState.isValid
		|| cardState.errors !== nextCardState.errors
		|| cardState.lastEvent !== nextCardState.lastEvent;
}

function getTokenValue(tokenResult: ConvesioPayTokenResult): string {
	if (typeof tokenResult === 'string') {
		return tokenResult.trim();
	}

	const token = tokenResult.token ?? tokenResult.id ?? '';
	return token.trim();
}

export function clearConvesioPayCardToken(): void {
	clearCachedConvesioPayCardToken();
	tokenizationInFlight = null;
}

export function updateConvesioPayCardStateFromChange(event: unknown): void {
	const cardEvent = event as {isValid?: unknown; errors?: unknown[]};
	const nextCardState = {
		isValid: cardEvent.isValid === true,
		errors: Array.isArray(cardEvent.errors) ? cardEvent.errors : emptyCardState.errors,
		lastEvent: event,
	};
	const didCardStateChange = hasConvesioPayCardStateChanged(nextCardState);
	const isValidRefreshDuringTokenization = isCardTokenizationActive
		&& cardState.isValid
		&& nextCardState.isValid
		&& nextCardState.errors.length === 0;

	cardState = {
		...nextCardState,
	};

	if (!didCardStateChange || isValidRefreshDuringTokenization) {
		return;
	}

	cardStateRevision += 1;
	clearCachedConvesioPayCardToken();
}

export function isConvesioPayCardReady(): boolean {
	return cardState.isValid;
}

export function isConvesioPayCardTokenRequiredForSubmit(
	selectedMethod: ConvesioPayMethod | undefined,
	hasBtcpayPaymentData: boolean,
	hasApplePayPaymentData: boolean,
	hasGooglePayPaymentData: boolean,
): boolean {
	if (hasBtcpayPaymentData || hasApplePayPaymentData || hasGooglePayPaymentData) {
		return false;
	}

	return selectedMethod === undefined || selectedMethod === 'card';
}

export async function ensureConvesioPayCardToken(component: ConvesioPayCardComponent | null | undefined): Promise<string> {
	if (cachedCardToken) {
		(window as unknown as Record<string, unknown>)['convesiopayPaymentToken'] = cachedCardToken;
		return cachedCardToken;
	}

	if (tokenizationInFlight) {
		return tokenizationInFlight;
	}

	if (!isConvesioPayCardReady()) {
		throw new Error('Please complete all required payment fields');
	}

	if (!component || typeof component.createToken !== 'function') {
		throw new Error('ConvesioPay card component is not ready. Please try again.');
	}

	const revisionAtTokenizationStart = cardStateRevision;
	isCardTokenizationActive = true;

	tokenizationInFlight = (async () => {
		const tokenResult = await component.createToken!();
		const token = getTokenValue(tokenResult);

		if (!token) {
			throw new Error('Payment token is missing. Please try again.');
		}

		if (revisionAtTokenizationStart !== cardStateRevision || !isConvesioPayCardReady()) {
			throw new Error('Card details changed while creating the payment token. Please try again.');
		}

		cachedCardToken = token;
		(window as unknown as Record<string, unknown>)['convesiopayPaymentToken'] = token;
		return token;
	})();

	try {
		return await tokenizationInFlight;
	} catch (error) {
		clearConvesioPayCardToken();
		throw error;
	} finally {
		tokenizationInFlight = null;
		isCardTokenizationActive = false;
	}
}
